// ФТ-6.1. Фотоархив объекта: все фото из отчётов, работ и проблем с фильтром по дате, этажу, захватке и работе.
// Фото сгруппированы по дням; по нажатию — крупно, с автором, временем, этажом, захваткой и работой.

import { useEffect, useMemo, useState } from "react";
import { addDays, fmtDate, fmtDateTime, localISODate } from "../../shared/format";
import { setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Empty, Field, Input, Loading, Select, useAsync, useEscape } from "../../shared/ui";
import { api, floorAbbr, floorName } from "../ctx";

type Photo = {
  id: string; floor: number | null; grip: string | null; taken_at: string; caption: string | null;
  author: string | null; work: string | null; source: "issue" | "task" | "report" | "other"; url: string; thumb_url: string;
};

const SOURCE: Record<Photo["source"], string> = { issue: "проблема", task: "работа", report: "отчёт смены", other: "" };

/** Конец дня по местному времени — чтобы «по 15.10» включало фото, снятые вечером 15-го. */
const dayEnd = (d: string) => new Date(`${d}T23:59:59`).toISOString();
const dayStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();

export function PhotosPage({ id }: { id: string }) {
  const r = useRoute();
  const q = r.query;
  const from = q.get("from") || addDays(localISODate(), -30);
  const to = q.get("to") || "";
  const floor = q.get("floor") || "";
  const grip = q.get("grip") || "";
  const work = q.get("work") || "";
  const grips = useAsync<any[]>(() => api.get(`/buildings/${id}/grips`), [id]);
  const works = useAsync<any[]>(() => api.get("/work-types"), []);
  const list = useAsync<Photo[]>(() => api.get("/photos", {
    building: id, floor, grip, work, date_from: from ? dayStart(from) : undefined, date_to: to ? dayEnd(to) : undefined,
  }), [id, from, to, floor, grip, work]);
  const [open, setOpen] = useState<number | null>(null);
  const items = list.data || [];
  const groups = useMemo(() => {
    const m = new Map<string, { p: Photo; i: number }[]>();
    items.forEach((p, i) => {
      const d = localISODate(new Date(p.taken_at));
      m.set(d, [...(m.get(d) || []), { p, i }]);
    });
    return [...m.entries()];
  }, [items]);
  const set = (k: string, v: string) => setQuery({ [k]: v || null });
  const filtered = !!(to || floor || grip || work || q.get("from"));
  // список этажей — с подземными (−1, −2…) и добавленными сверху, а не 1..N
  const fl = useAsync<{ number: number }[]>(() => api.get(`/buildings/${id}/floors`), [id]);
  const floorOpts = (fl.data || []).map((f) => ({ value: String(f.number), label: floorName(f.number) }));
  return (
    <>
      <TopBar title="Фотоархив" />
      <div className="content stack">
        <div className="panel pad">
          <div className="filters">
            <Field label="С"><Input type="date" value={from} onChange={(e) => set("from", e.target.value)} /></Field>
            <Field label="По"><Input type="date" value={to} onChange={(e) => set("to", e.target.value)} /></Field>
            <Field label="Этаж"><Select value={floor} onChange={(e) => set("floor", e.target.value)} placeholder="все"
              options={floorOpts} /></Field>
            <Field label="Захватка"><Select value={grip} onChange={(e) => set("grip", e.target.value)} placeholder="все"
              options={(grips.data || []).map((g) => ({ value: g.code, label: g.code }))} /></Field>
            <Field label="Работа"><Select value={work} onChange={(e) => set("work", e.target.value)} placeholder="все"
              options={(works.data || []).map((w) => ({ value: w.code, label: w.short_name }))} /></Field>
            {filtered && <Button variant="ghost" onClick={() => setQuery({ from: null, to: null, floor: null, grip: null, work: null })}>Сбросить</Button>}
          </div>
          {work && <div className="muted small" style={{ marginTop: 8 }}>По работе отбираются фото, снятые к работе в отчёте прораба.</div>}
        </div>
        {!list.data ? <Loading error={list.error} onRetry={list.reload} /> : items.length === 0 ? (
          <Empty>Фото по этим условиям нет. Прорабы прикладывают фото в отчёте смены — к работе или к проблеме.</Empty>
        ) : (
          <>
            <div className="muted small">Найдено фото: {items.length}{items.length >= 500 ? " (показаны последние 500 — сузьте период)" : ""}</div>
            {groups.map(([day, ps]) => (
              <div key={day} className="panel pad stack tight">
                <h3>{fmtDate(day)} <span className="muted small">· {ps.length} фото</span></h3>
                <div className="photos">
                  {ps.map(({ p, i }) => (
                    <button key={p.id} type="button" className="photo-tile" onClick={() => setOpen(i)} title={p.caption || ""}>
                      <img src={api.base + `/photos/${p.id}/file?thumb=true&token=${api.token}`} alt={p.caption || "фото"} loading="lazy" />
                      <span>{[p.floor != null ? floorAbbr(p.floor) : null, p.grip].filter(Boolean).join(" · ") || " "}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      {open != null && items[open] && <Lightbox items={items} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </>
  );
}

function Lightbox({ items, index, onIndex, onClose }: { items: Photo[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  useEscape(onClose);
  const p = items[index];
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
      if (e.key === "ArrowRight" && index < items.length - 1) onIndex(index + 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [index, items.length, onIndex]);
  const full = api.base + `/photos/${p.id}/file?token=${api.token}`;
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <div className="lightbox" role="dialog" aria-modal="true" aria-label="Фото">
        <img src={full} alt={p.caption || "фото"} />
        <div className="cap">
          <div className="row between">
            <b>{p.caption || (p.work ? p.work : "Фото")}</b>
            <span className="muted small">{index + 1} из {items.length}</span>
          </div>
          <div className="small">
            {fmtDateTime(p.taken_at)}{p.author ? ` · ${p.author}` : ""}
            {p.floor != null ? ` · ${floorName(p.floor, false)}` : ""}{p.grip ? ` · ${p.grip}` : ""}{p.work ? ` · ${p.work}` : ""}
            {SOURCE[p.source] ? ` · ${SOURCE[p.source]}` : ""}
          </div>
          <div className="row">
            <Button size="small" disabled={index === 0} onClick={() => onIndex(index - 1)}>← Назад</Button>
            <Button size="small" disabled={index === items.length - 1} onClick={() => onIndex(index + 1)}>Вперёд →</Button>
            <a className="btn small ghost" href={full} target="_blank" rel="noreferrer">Открыть оригинал</a>
            <Button size="small" variant="ghost" onClick={onClose}>Закрыть</Button>
          </div>
        </div>
      </div>
    </>
  );
}
